import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import type { SetupStepKey } from '@hrms/shared';
import { useCoreSetupProgress } from './SetupNavItem.js';
import { FROM_SETUP_PARAM, SETUP_STEPS } from './setupSteps.js';

const [PARAM_NAME = 'from', PARAM_VALUE = 'setup'] = FROM_SETUP_PARAM.split('=');

/** A path without its query or a trailing slash, so `/x/` and `/x?tab=1` both read as `/x`. */
function bare(path: string): string {
  const withoutQuery = path.split('?')[0] ?? path;
  return withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, '') : withoutQuery;
}

/** "Step n of total: label" when `pathname` is the screen of one of the core steps. */
export function currentCoreStep(
  pathname: string,
  steps: readonly SetupStepKey[],
): { number: number; total: number; label: string } | null {
  const here = bare(pathname);
  const index = steps.findIndex((key) => bare(SETUP_STEPS[key].to) === here);
  if (index === -1) return null;
  return { number: index + 1, total: steps.length, label: SETUP_STEPS[steps[index]!].label };
}

/**
 * A slim bar over the page while someone works through setup: shown only when the screen was
 * opened from the Setup page (`?from=setup`, see `stepHref`). It names the step when the screen is
 * one of the core steps, links back to /setup, and can be dismissed, which drops the param.
 */
export function BackToSetupBar() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const progress = useCoreSetupProgress();

  if (searchParams.get(PARAM_NAME) !== PARAM_VALUE) return null;

  const step = progress ? currentCoreStep(location.pathname, progress.steps) : null;

  const dismiss = () => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(PARAM_NAME);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div
      role="region"
      aria-label="Setup progress"
      className="flex items-center gap-2 border-b border-border bg-accent-subtle px-4 py-2 text-sm text-fg sm:gap-3 sm:px-6"
    >
      <p className="min-w-0 flex-1">
        <span className="font-semibold">Setting up your workspace</span>
        {step && (
          <span className="text-fg-muted">
            {' · '}Step {step.number} of {step.total}
            {/* The step's name is in the page's own heading; on a phone the bar keeps to the count. */}
            <span className="hidden sm:inline">: {step.label}</span>
          </span>
        )}
      </p>
      <Link
        to="/setup"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 font-medium text-accent transition-colors hover:bg-surface"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to setup
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="flex size-10 shrink-0 sm:size-8 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface hover:text-fg"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
